/**
 * El historial de versiones, leido del CHANGELOG.md del repositorio.
 *
 * El fichero se mete en el bundle tal cual con `?raw` y se parte aqui. Lo
 * importante es que **no hay una segunda copia que mantener**: el historial
 * que se escribe al publicar un cambio es el mismo que lee la app, asi que no
 * puede quedarse atras. Son 19 KB en crudo, unos 6 comprimidos.
 *
 * El markdown se parsea a mano en vez de traerse una libreria: el fichero usa
 * cuatro cosas —titulos `##`, parrafos, listas con `-` y, dentro, negrita,
 * cursiva y `codigo`— y un `marked` cualquiera pesa mas que el propio
 * historial. Si algun dia el CHANGELOG usa algo mas, aqui se vera: lo que no
 * se reconoce sale como texto plano, nunca como un simbolo suelto.
 */

import crudo from '../../CHANGELOG.md?raw'

/** Un trozo de texto con su forma: normal, negrita, cursiva o codigo. */
export interface Pieza {
  texto: string
  tipo: 'normal' | 'fuerte' | 'enfasis' | 'codigo'
}

export type Bloque =
  | { tipo: 'parrafo'; piezas: Pieza[] }
  | { tipo: 'lista'; puntos: Pieza[][] }

export interface Version {
  /** "1.3.2", o el titulo tal cual si no es un numero de version. */
  nombre: string
  /** "2026-09-11", o cadena vacia si el titulo no la trae. */
  fecha: string
  bloques: Bloque[]
}

/** Las marcas de cada forma, con el tipo de pieza que producen. */
const MARCAS: { abre: string; tipo: Pieza['tipo'] }[] = [
  { abre: '**', tipo: 'fuerte' },
  { abre: '`', tipo: 'codigo' },
  { abre: '*', tipo: 'enfasis' },
]

/**
 * Parte una linea en piezas segun las marcas de markdown.
 *
 * Va de izquierda a derecha buscando la marca que abre antes. La negrita se
 * mira antes que la cursiva a proposito: `**` empieza por `*` y al reves se
 * comeria la mitad de la marca.
 */
export function piezasDe(linea: string): Pieza[] {
  const piezas: Pieza[] = []
  let resto = linea

  while (resto) {
    let mejor: { desde: number; hasta: number; abre: string; tipo: Pieza['tipo'] } | null = null
    for (const { abre, tipo } of MARCAS) {
      const desde = resto.indexOf(abre)
      if (desde === -1) continue
      const hasta = resto.indexOf(abre, desde + abre.length)
      // Sin marca de cierre no hay forma que valga: es un asterisco suelto
      if (hasta === -1) continue
      if (!mejor || desde < mejor.desde) mejor = { desde, hasta, abre, tipo }
    }

    if (!mejor) {
      piezas.push({ texto: resto, tipo: 'normal' })
      break
    }
    if (mejor.desde > 0) {
      piezas.push({ texto: resto.slice(0, mejor.desde), tipo: 'normal' })
    }
    const dentro = resto.slice(mejor.desde + mejor.abre.length, mejor.hasta)
    if (dentro) piezas.push({ texto: dentro, tipo: mejor.tipo })
    resto = resto.slice(mejor.hasta + mejor.abre.length)
  }

  return piezas
}

/**
 * Junta las lineas en bloques: parrafos y listas.
 *
 * El CHANGELOG va con las lineas partidas a ochenta columnas, asi que un
 * parrafo son varias lineas seguidas y hay que volver a unirlas; si no, en el
 * movil se veria cada linea cortada por donde la corto el editor. Los puntos
 * de una lista siguen el mismo cuento: lo que va sangrado debajo de un `-` es
 * continuacion del mismo punto.
 */
function bloquesDe(lineas: string[]): Bloque[] {
  const bloques: Bloque[] = []
  let parrafo: string[] = []
  let puntos: string[] = []

  const cerrarParrafo = () => {
    if (parrafo.length) {
      bloques.push({ tipo: 'parrafo', piezas: piezasDe(parrafo.join(' ')) })
      parrafo = []
    }
  }
  const cerrarLista = () => {
    if (puntos.length) {
      bloques.push({ tipo: 'lista', puntos: puntos.map(piezasDe) })
      puntos = []
    }
  }

  for (const linea of lineas) {
    const limpia = linea.trim()
    if (!limpia || limpia === '---') {
      cerrarParrafo()
      cerrarLista()
      continue
    }
    if (limpia.startsWith('- ')) {
      cerrarParrafo()
      puntos.push(limpia.slice(2))
      continue
    }
    if (puntos.length) {
      // Sangrado debajo de un punto: sigue el mismo punto
      puntos[puntos.length - 1] += ' ' + limpia
      continue
    }
    parrafo.push(limpia)
  }
  cerrarParrafo()
  cerrarLista()
  return bloques
}

/** Separa "1.3.2 — 2026-09-11" en sus dos partes. */
function partirTitulo(titulo: string): { nombre: string; fecha: string } {
  const guion = titulo.indexOf('—')
  if (guion === -1) return { nombre: titulo.trim(), fecha: '' }
  return { nombre: titulo.slice(0, guion).trim(), fecha: titulo.slice(guion + 1).trim() }
}

function parsear(texto: string): { intro: Bloque[]; versiones: Version[] } {
  const lineas = texto.split('\n')
  const intro: string[] = []
  const versiones: Version[] = []
  let actual: { titulo: string; lineas: string[] } | null = null

  for (const linea of lineas) {
    if (linea.startsWith('## ')) {
      if (actual) {
        const { nombre, fecha } = partirTitulo(actual.titulo)
        versiones.push({ nombre, fecha, bloques: bloquesDe(actual.lineas) })
      }
      actual = { titulo: linea.slice(3), lineas: [] }
      continue
    }
    // El titulo del documento no pinta nada dentro de la app: ya se sabe que
    // es el historial de Yomou porque se abrio desde ahi.
    if (linea.startsWith('# ')) continue
    if (actual) actual.lineas.push(linea)
    else intro.push(linea)
  }
  if (actual) {
    const { nombre, fecha } = partirTitulo(actual.titulo)
    versiones.push({ nombre, fecha, bloques: bloquesDe(actual.lineas) })
  }

  return { intro: bloquesDe(intro), versiones }
}

const historial = parsear(crudo)

/** Los apartados del CHANGELOG, del mas nuevo al mas viejo. */
export const VERSIONES = historial.versiones

/** Los parrafos de arriba del todo: de donde salen estos numeros y fechas. */
export const INTRO = historial.intro
